// Ödeme öncesi il / ilçe seçici (snippets/es-adres-secici.liquid, snippets/es-hemen-al-penceresi.liquid).
// Shopify TR checkout'unda tek bir "İl ve İlçe" metin kutusu var; burada seçtirip
// Storefront API ile o kutuyu "İlçe / İl" olarak önceden dolduruyoruz.

const SF_URL = '/api/2025-10/graphql.json';
const TIMEOUT_MS = 4000;

const alfabetik = new Intl.Collator('tr');

let dataPromise;
const loadData = (src) => (dataPromise ??= fetch(src).then((r) => r.json()));

const withTimeout = (promise) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, TIMEOUT_MS))]);

class EsAdresSecici extends HTMLElement {
  connectedCallback() {
    if (this.ilSelect) return;
    this.ilSelect = this.querySelector('[name="es-il"]');
    this.ilceSelect = this.querySelector('[name="es-ilce"]');
    this.hata = this.querySelector('.es-adres__hata');

    this.ilSelect.addEventListener('change', () => {
      this.fillIlce();
      this.clearError();
      this.rememberSelection();
      this.saveAttributes();
    });
    this.ilceSelect.addEventListener('change', () => {
      this.clearError();
      this.rememberSelection();
      this.saveAttributes();
    });

    // Horizon sepeti her güncellemede sunucu HTML'iyle morph'luyor. data-skip-node-update
    // yalnızca bu elemanın özniteliklerini korur, alt öğeleri değil: <option>'lar silinip
    // seçim sıfırlanıyor (sepette 2+ ürün varken çekmece morph'lanır). Listeyi geri kur.
    new MutationObserver(() => this.restoreAfterMorph()).observe(this, { childList: true, subtree: true });

    loadData(this.dataset.src)
      .then((data) => {
        this.data = data;
        this.fillIl();
        this.rememberSelection();
      })
      .catch(() => {
        // Veri yüklenemezse ödemeyi engellemiyoruz.
        this.failed = true;
      });
  }

  // Plaka kodları ("10", "34"...) nesne anahtarı olarak sayısal sıraya girer; bu yüzden burada sıralıyoruz.
  fillIl(savedCode, savedIlce = this.dataset.ilce) {
    const saved = savedCode === undefined ? this.dataset.il : this.data[savedCode]?.n;
    const iller = Object.entries(this.data).sort(([, a], [, b]) => alfabetik.compare(a.n, b.n));
    this.ilSelect.length = 1;
    for (const [code, il] of iller) {
      const option = new Option(il.n, code);
      if (il.n === saved) option.selected = true;
      this.ilSelect.add(option);
    }
    if (!saved) this.ilSelect.selectedIndex = 0;
    this.fillIlce(savedIlce);
  }

  rememberSelection() {
    this.secim = { il: this.ilSelect.value, ilce: this.ilceSelect.value };
  }

  // Morph sonrası liste eksildiyse veya seçim kaydıysa son seçimle yeniden doldurur.
  restoreAfterMorph() {
    if (!this.data || !this.secim) return;
    const { il, ilce } = this.secim;
    const intact =
      this.ilSelect.length > 1 &&
      this.ilSelect.value === il &&
      (!il || this.ilceSelect.length > 1) &&
      this.ilceSelect.value === ilce &&
      this.ilceSelect.disabled === !il;
    if (intact) return;
    this.fillIl(il, ilce);
  }

  fillIlce(saved) {
    const il = this.data?.[this.ilSelect.value];
    this.ilceSelect.length = 1;
    this.ilceSelect.options[0].text = il ? 'İlçe seçin' : 'Önce il seçin';
    this.ilceSelect.disabled = !il;
    if (!il) return;
    for (const name of [...il.d].sort(alfabetik.compare)) {
      const option = new Option(name, name);
      if (name === saved) option.selected = true;
      this.ilceSelect.add(option);
    }
  }

  get selection() {
    const il = this.data?.[this.ilSelect.value];
    const ilce = this.ilceSelect.value;
    return il && ilce ? { il: il.n, ilce } : null;
  }

  get isReady() {
    return Boolean(this.failed || this.selection);
  }

  showError() {
    this.classList.add('es-adres--hatali');
    this.hata.hidden = false;
    const target = this.ilSelect.value ? this.ilceSelect : this.ilSelect;
    target.setAttribute('aria-invalid', 'true');
    this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  }

  clearError() {
    this.classList.remove('es-adres--hatali');
    this.hata.hidden = true;
    this.ilSelect.removeAttribute('aria-invalid');
    this.ilceSelect.removeAttribute('aria-invalid');
  }

  // İstekler sıraya alınır; böylece son seçim en son yazılır.
  saveAttributes() {
    const sel = this.selection;
    const body = JSON.stringify({ attributes: { 'İl': sel?.il ?? '', 'İlçe': sel?.ilce ?? '' } });
    this.saving = (this.saving ?? Promise.resolve()).then(() =>
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {})
    );
    return this.saving;
  }

  get deliveryAddress() {
    const sel = this.selection;
    return { countryCode: 'TR', city: `${sel.ilce} / ${sel.il}` };
  }

  storefront(query, variables) {
    return fetch(SF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.dataset.token,
      },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());
  }

  async prefillCheckout() {
    if (!this.selection) return;
    const cart = await fetch('/cart.js').then((r) => r.json());
    await this.storefront(
      `mutation($id: ID!, $a: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesReplace(cartId: $id, addresses: $a) { userErrors { message } }
      }`,
      {
        id: `gid://shopify/Cart/${cart.token}`,
        a: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: this.deliveryAddress } }],
      }
    );
  }

  // "Hemen satın alın": mevcut sepete dokunmadan yalnızca bu ürünle yeni sepet açar.
  async buyNowUrl(variantId, quantity) {
    const sel = this.selection;
    const result = await this.storefront(
      `mutation($input: CartInput!) {
        cartCreate(input: $input) { cart { checkoutUrl } userErrors { message } }
      }`,
      {
        input: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
          attributes: [
            { key: 'İl', value: sel.il },
            { key: 'İlçe', value: sel.ilce },
          ],
          delivery: {
            addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: this.deliveryAddress } }],
          },
        },
      }
    );
    return result?.data?.cartCreate?.cart?.checkoutUrl;
  }
}

// Cart drawer da bir <dialog>; yalnızca "Hemen satın alın" penceresindeki seçici hariç tutulur.
const SEPET_SECICISI = 'es-adres-secici:not([data-es-hemen-al] es-adres-secici)';

// Ödeme butonu: seçim yoksa durdur, varsa adresi checkout'a aktarıp yönlendir.
async function onCheckoutClick(event, button) {
  const scope = button.closest('.cart-drawer__summary, cart-items-component') ?? document;
  const secici = scope.querySelector(SEPET_SECICISI) ?? document.querySelector(SEPET_SECICISI);
  if (!secici || secici.failed) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (!secici.isReady) {
    secici.showError();
    return;
  }

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  try {
    await withTimeout(Promise.all([secici.saveAttributes(), secici.prefillCheckout()]));
  } catch {
    // Ön doldurma başarısız olsa da müşteri checkout'a gidebilsin.
  }
  window.location.href = '/checkout';
}

// "Hemen satın alın": Shopify'ın butonunu durdurup il / ilçe penceresini açar.
function onBuyNowClick(event, host) {
  const dialog = document.querySelector('dialog[data-es-hemen-al]');
  const secici = dialog?.querySelector('es-adres-secici');
  const form =
    host.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]') ??
    host.closest('form');
  if (!dialog || !secici || secici.failed || !form?.elements.namedItem('id')) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  dialog.esForm = form;
  dialog.showModal();
}

async function onBuyNowSubmit(event, dialog) {
  if (event.submitter?.value !== 'devam') return;
  event.preventDefault();

  const secici = dialog.querySelector('es-adres-secici');
  if (!secici.isReady) {
    secici.showError();
    return;
  }

  const form = dialog.esForm;
  const variantId = form.elements.namedItem('id').value;
  const quantity = Math.max(1, parseInt(form.elements.namedItem('quantity')?.value, 10) || 1);
  event.submitter.setAttribute('aria-busy', 'true');

  let url;
  try {
    url = await withTimeout(secici.buyNowUrl(variantId, quantity));
  } catch {
    // Aşağıdaki yedek yol devreye girer.
  }
  // Yedek: cart permalink, aynı "hemen al" davranışı (adres ön doldurması olmadan).
  window.location.href = url ?? `/cart/${variantId}:${quantity}`;
}

if (!customElements.get('es-adres-secici')) {
  customElements.define('es-adres-secici', EsAdresSecici);

  document.addEventListener(
    'click',
    (event) => {
      const checkout = event.target.closest?.('button[name="checkout"]');
      if (checkout) return onCheckoutClick(event, checkout);

      const buyNow = event
        .composedPath()
        .find((el) => el instanceof Element && el.matches('shopify-accelerated-checkout, .shopify-payment-button'));
      if (buyNow && !buyNow.closest('#cart-drawer, cart-items-component')) onBuyNowClick(event, buyNow);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const dialog = event.target.closest('dialog[data-es-hemen-al]');
      if (dialog) onBuyNowSubmit(event, dialog);
    },
    true
  );
}
